import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import {
  callIfFunction,
  focusAfterTimeout,
  isDevelopment,
  getIcon,
  emulatingDevelopment,
} from './../../util';

import {
  MessageModal,
  ConfirmModal
} from '../modals';

import {
  Localize
} from '../shared';

import {
  localizeToString
} from '../../util/localizers';

const LIKERT_QUESTIONS_PER_PAGE = 5;
const CHOICE_QUESTIONS_PER_PAGE = 1;
const FREEFORM_QUESTIONS_PER_PAGE = 2;

const LIKERT_RESPONSES = [{
  value: 1,
  label: <Localize id="assessment_likert_choice_1">Very Strongly Disagree</Localize>,
},{
  value: 2,
  label: <Localize id="assessment_likert_choice_2">Strongly Disagree</Localize>,
},{
  value: 3,
  label: <Localize id="assessment_likert_choice_3">Disagree</Localize>,
},{
  value: 4,
  label: <Localize id="assessment_likert_choice_4">Neutral</Localize>,
},{
  value: 5,
  label: <Localize id="assessment_likert_choice_5">Agree</Localize>,
},{
  value: 6,
  label: <Localize id="assessment_likert_choice_6">Strongly Agree</Localize>,
},{
  value: 7,
  label: <Localize id="assessment_likert_choice_7">Very Strongly Agree</Localize>,
}]

class AssessmentView extends Component {

  state = {
    currentPage: 0,
    assessmentPages: false,
    savingProgress: false,
    exitingAfterSave: false,
    showAssessmentComplete: false,
    showAssessmentSubmitted: false,
    submittingAssessmentForScoring: false,
  }

  componentDidUpdate(prevProps, prevState){
    //if page changes, need to scroll to top
    if(this.state.currentPage !== prevState.currentPage){
      window.scrollTo(0,60);
    }
  }

  buildAssessmentPages(callback){
    const choiceQuestions = _.filter(this.orderedQuestions(), { questionFormat: 'choice' });
    const likertQuestions = _.filter(this.orderedQuestions(), { questionFormat: 'likert' });
    const freeformQuestions = _.filter(this.orderedQuestions(), { questionFormat: 'freeform' });

    const choiceQuestionCount = choiceQuestions.length;
    const likertQuestionCount = likertQuestions.length;
    const freeformQuestionCount = freeformQuestions.length;

    const choicePageCount = Math.ceil(choiceQuestionCount / CHOICE_QUESTIONS_PER_PAGE);
    const likertPageCount = Math.ceil(likertQuestionCount / LIKERT_QUESTIONS_PER_PAGE);
    const freeformPageCount = Math.ceil(freeformQuestionCount / FREEFORM_QUESTIONS_PER_PAGE);

    let assessmentPages = [];

    if(choicePageCount > 0){
      assessmentPages.push({
        type: 'explanation',
        title: <Localize id="assessment_skills_section_name">Skills Inventory</Localize>,
        explanation: <Localize id="assessment_skills_intro">'To help you leverage your strengths and improve sales performance, VELOCITY.win evaluates your sales Talents and Skills, and compares them to what top performers like you do to win. The first step is a short series of multiple-choice questions called the Skills Inventory, which will require approximately ten minutes to complete. You will be asked to provide your Top and Bottom responses to each situation. Your results will reveal strategies to accelerate your sales cycle and improve your success rates. Through sales training and experience, you can improve your VELOCITY.win Skills Score.'</Localize>,
        numberQuestions: choiceQuestionCount
      });

      for(let i = 0; i < choicePageCount; i++){
        const questionsStart = i * CHOICE_QUESTIONS_PER_PAGE;
        const questionsEnd = questionsStart + CHOICE_QUESTIONS_PER_PAGE;

        assessmentPages.push({
          type: 'questions',
          questionFormat: 'choice',
          questions: _.slice(choiceQuestions, questionsStart, questionsEnd)
        });
      }
    }

    if(likertPageCount > 0){
      assessmentPages.push({
        type: 'explanation',
        title: <Localize id="assessment_talent_section_name">Talent Inventory</Localize>,
        explanation: <Localize id="assessment_talent_intro">'The Talent Inventory will take you about ten minutes to identify your leading talents and areas of greatest potential impact in selling and leading a sales team. For each question in the Talent Inventory, please rate your level of agreement on a scale from left to right with "Very Strongly Disagree" on the FAR LEFT to "Very Strongly Agree" on the FAR RIGHT. There are no right or wrong answers. It is in your best interest to answer each unique item as honestly as you can to reflect its relative importance to you.'</Localize>,
        numberQuestions: likertQuestionCount
      });

      for(let i = 0; i < likertPageCount; i++){
        const questionsStart = i * LIKERT_QUESTIONS_PER_PAGE;
        const questionsEnd = questionsStart + LIKERT_QUESTIONS_PER_PAGE;

        assessmentPages.push({
          type: 'questions',
          questionFormat: 'likert',
          questions: _.slice(likertQuestions, questionsStart, questionsEnd)
        });
      }
    }

    if(freeformPageCount > 0){
      assessmentPages.push({
        type: 'explanation',
        title: 'Open-Ended Questions',
        explanation: 'Please provide a brief response to each optional question below. To ensure successful submission of your responses, it is encouraged to complete the following questions in this sitting.',
        numberQuestions: freeformQuestionCount,
      });

      for(let i = 0; i < freeformPageCount; i++){
        const questionsStart = i * FREEFORM_QUESTIONS_PER_PAGE;
        const questionsEnd = questionsStart + FREEFORM_QUESTIONS_PER_PAGE;

        assessmentPages.push({
          type: 'questions',
          questionFormat: 'freeform',
          questions: _.slice(freeformQuestions, questionsStart, questionsEnd)
        });
      }
    }

    assessmentPages = _.map(assessmentPages, (page, pageIndex) => {
      return {
        ...page,
        pageIndex,
      }
    });

    this.setState({ assessmentPages }, () => {
      callIfFunction(callback);
    });
  }

  currentPage(){

    const { currentPage, assessmentPages } = this.state;

    //assessmentPages are zero index
    if(!currentPage || !assessmentPages || !assessmentPages[currentPage - 1]){
      return false;
    }

    return assessmentPages[currentPage - 1];
  }

  assessmentQuestions(){
    return this.props.assessment.questions;
  }

  orderedQuestions(){
    return _.sortBy(this.assessmentQuestions(), question => {
      return question.questionIndex;
    });
  }

  assessmentPartiallyComplete(){
    return _.size(this.props.assessment.responses) > 0;
  }

  getPageForLastRespondedToQuestion(){
    const { assessmentPages } = this.state;

    const pageCount = assessmentPages.length;

    let lastInProgressPage = null;
    let checkingPageIndex = pageCount - 1;

    while(lastInProgressPage === null){

      const pageQuestions = assessmentPages[checkingPageIndex].questions || [];

      let allPageQuestionsAnswered = true;

      _.each(pageQuestions, question => {
        if(this.getQuestionResponse(question.id)){
          lastInProgressPage = checkingPageIndex;
        }
        else{
          allPageQuestionsAnswered = false;
        }
      });

      //if this is the last in progress page, AND all questions on this page are answered,
      //we need to increase this by one to move onto next page
      //unless we're already on the last page of the assessment, then just show that page
      if(lastInProgressPage && allPageQuestionsAnswered && lastInProgressPage < pageCount - 1){
        lastInProgressPage++;
      }

      //continue iterating
      if(!lastInProgressPage){
        checkingPageIndex--;

        if(checkingPageIndex === 0){
          //none in progress
          lastInProgressPage = 1;
        }
      }
    }

    return lastInProgressPage;
  }

  goToPageForLastRespondedToQuestion(){

    //adjust since lastInProgressPage is zero-indexed, whereas current page is not
    this.setState({
      currentPage: this.getPageForLastRespondedToQuestion() + 1
    });
  }

  componentWillMount(){
    //console.log('mounting with assessment: ', this.props.assessment);

    this.buildAssessmentPages(() => {
      if(!this.assessmentPartiallyComplete()){
        this.setState({
          currentPage: 1
        });
      }
      else{
        //current page = 0
      }
      // if(this.assessmentPartiallyComplete()){
      //   this.goToPageForLastRespondedToQuestion();
      // }
    });
  }

  renderInitialPage(){

    const profile = this.props.assessment.assessmentConfig.profile;
    const responses = this.props.assessment.responses;
    const assessmentIsInProgress = _.size(responses) > 0;
    //const percentComplete = this.getProgress();
    const pageProgress = this.getPageProgress();

    return (
      <div id="assessment-page-0" className="assessment-page">
        <h2>{this.props.orgDisplayName} <Localize id="txt_assessment_label">Assessment</Localize></h2>
        <p><Localize replaceWith={this.props.orgDisplayName} id="txt_assessment_in_progress_description">Your assessment is currently in progress.</Localize></p>

        <div className="page-progress">
          <div className="progress-bar-container">
            <div className="progress-bar" style={{ width: pageProgress.lastCompletedPageProgress + '%' }}></div>
          </div>
          <div className="progress-label">{pageProgress.lastCompletedPageProgress}% <Localize id="txt_complete" /></div>
        </div>

        <div className="page-actions">
          <button
            onClick={this.goToPageForLastRespondedToQuestion.bind(this)}
            className="button blue-button"><Localize id="btn_resume_assessment">Resume Assessment</Localize></button>
        </div>
      </div>
    )
  }

  renderAssessmentExplanationPage(){
    const { title, explanation, pageIndex, numberQuestions } = this.currentPage();

    return (
      <div id={`assessment-page-${pageIndex}`} className="assessment-page">
        <h2>{title}</h2>
        <p className="larger">{numberQuestions} {numberQuestions > 1 && <Localize id="txt_questions">questions</Localize>}{numberQuestions === 1 && <Localize id="txt_question">question</Localize>}</p>
        <p>{explanation}</p>

        <div className="page-actions">
          <button
            onClick={this.goToNextPage.bind(this)}
            className="button blue-button"><Localize id="btn_continue">Continue</Localize></button>
          <button
            onClick={this.exit.bind(this)}
            className="button grey-outline cancel-action"><Localize id="btn_save_and_exit">Save & Exit</Localize></button>
        </div>
      </div>
    )
  }

  renderFreeformQuestion(question){
    const {
      text,
      id,
    } = question;

    const questionResponse = this.getQuestionResponse(question.id);

    const questionHasResponse = questionResponse && questionResponse !== '';

    return (
      <div key={`assessment-question-${id}`} className={`question freeform active ${questionHasResponse ? 'complete' : ''}`}>
        <div className="text"><Localize id={`assessment_question_${id}_text`}>{text}</Localize></div>
        <textarea value={questionResponse} onChange={e => { this.props.updateQuestionResponse(question.id, e.target.value); }}></textarea>
      </div>
    );
  }

  renderChoiceQuestion(question){
    const {
      text,
      choices,
      id,
    } = question;

    const questionResponse = this.getQuestionResponse(question.id);

    const questionResponseHasTop = questionResponse && questionResponse.top;
    const questionResponseHasBottom = questionResponse && questionResponse.bottom;

    return (
      <div key={`assessment-question-${id}`} className={`question choice ${questionResponse ? 'answered' : ''} active`}>
        <div className="text"><Localize id={`assessment_question_${id}_text`}>{text}</Localize></div>
        <div className="choices">
          {_.map(choices, (choice, index) => {

            const isTopChoice = questionResponse && questionResponse.top === index;
            const isBottomChoice = questionResponse && questionResponse.bottom === index;

            let updatedQuestionResponseIfClicked = { ...questionResponse };

            if(isTopChoice || isBottomChoice){
              //do nothing
            }
            else if(questionResponseHasTop && questionResponseHasBottom){
              //do nothing
            }
            else if(questionResponseHasTop){
              updatedQuestionResponseIfClicked.bottom = index;
            }
            else{
              updatedQuestionResponseIfClicked.top = index;
            }

            return (
              <div
                key={`assessment-question-${id}-response-${index}`}
                onClick={() => { if(isTopChoice || isBottomChoice){ return; } this.props.updateQuestionResponse(question.id, updatedQuestionResponseIfClicked); } }
                className={`choice-option ${isTopChoice ? 'selected is-top' : ''} ${isBottomChoice ? 'selected is-bottom' : ''} ${questionResponseHasTop && questionResponseHasBottom && 'responses-are-complete'}`}
              >
              <div className="choice-text"><Localize id={`assessment_question_${id}_choice_${index}`}>{choice}</Localize></div>
              {isTopChoice &&
                <div className="selected-tip">{getIcon('thumbs-up')} <Localize id="txt_top_choice">Top Choice</Localize></div>
              }
              {isBottomChoice &&
                <div className="selected-tip">{getIcon('thumbs-down', 'thumbs-down')} <Localize id="txt_bottom_choice">Bottom Choice</Localize></div>
              }
              {!isTopChoice && !isBottomChoice && questionResponseHasTop &&
                <div className="hover-tip"><Localize id="txt_mark_as_bottom">Mark as bottom choice</Localize></div>
              }
              {!isTopChoice && !isBottomChoice && !questionResponseHasTop &&
                <div className="hover-tip"><Localize id="txt_mark_as_top">Mark as top choice</Localize></div>
              }
            </div>
            )
          })}
        </div>
        <div
          className={`clear-choices ${questionResponseHasTop && 'visible'}`}
          onClick={() => { if(!questionResponseHasTop){ return; } this.props.updateQuestionResponse(question.id, { }); }}><Localize id="txt_clear_choices">clear choices</Localize></div>
      </div>
    );
  }

  getQuestionResponse(questionId){
    return this.props.assessment.responses[questionId];
  }

  previousQuestionOnPageIsAnswered(questionId){
    const allPageQuestions = this.currentPage().questions;

    const thisQuestionIndex = _.findIndex(allPageQuestions, { id: questionId });
    // console.log('thisQuestionIndex: ', thisQuestionIndex);
    // console.log('allPageQuestions: ', allPageQuestions);

    if(thisQuestionIndex === 0){
      return true;
    }
    else{
      return this.getQuestionResponse(allPageQuestions[thisQuestionIndex - 1].id) ? true : false;
    }
  }

  renderLikertQuestion(question){

    const {
      text,
      id,
    } = question;

    const questionResponse = this.getQuestionResponse(question.id);

    const previousQuestionOnPageIsAnswered = this.previousQuestionOnPageIsAnswered(id);

    const questionIsActive = previousQuestionOnPageIsAnswered || questionResponse;

    return (
      <div key={`assessment-question-${id}`} className={`question likert ${questionResponse ? 'answered' : ''} ${questionIsActive ? 'active' : ''}`}>
        <div className="text"><Localize id={`assessment_question_${id}_text`}>{text}</Localize></div>
        <div className="rating">
          {_.map(LIKERT_RESPONSES, ({label, value}) => {
            return (
              <div
                key={`assessment-question-${id}-response-${value}`}
                onClick={() => { if(!previousQuestionOnPageIsAnswered){ return; } this.props.updateQuestionResponse(question.id, (value !== questionResponse ? value : null)); } }
                className={`rating-option ${questionResponse === value ? 'selected' : ''}`}
              >{label}</div>
            )
          })}
        </div>
      </div>
    );
  }

  renderQuestion(question){
    switch(question.questionFormat){
      case 'likert':
        return this.renderLikertQuestion(question);
      case 'choice':
        return this.renderChoiceQuestion(question);
      case 'freeform':
        return this.renderFreeformQuestion(question);
    }
  }

  explanationForQuestionGroupType(questionGroupType){
    switch(questionGroupType){
      case 'likert':
        return <Localize id="assessment_likert_prompt">Please rate your level of agreement with each of the following statements using the scale provided</Localize>;
      case 'choice':
        return <Localize id="assessment_choice_prompt">For the scenario below, FIRST click on your TOP most desired choice, THEN click on your BOTTOM least desired choice.</Localize>;
      case 'freeform':
        return <Localize id="assessment_open_prompt">Please provide a brief response to each optional question below. To ensure successful submission of your responses, it is encouraged to complete the following questions in this sitting.</Localize>;
    }
  }

  getProgress(){
    const progressAsPercent = Math.floor((_.size(this.props.assessment.responses) * 100) / _.size(this.assessmentQuestions()));

    // console.log('progressAsPercent: ', _.size(this.props.assessment.responses), _.size(this.assessmentQuestions()));

    return progressAsPercent;
  }

  getPageProgress(){
    //const currentPage = this.getPageForLastRespondedToQuestion();
    const currentPage = this.state.currentPage;
    const totalPages = this.state.assessmentPages.length;
    const lastCompletedPage = this.getPageForLastRespondedToQuestion();

    // console.log('getPageProgress: ', currentPage, totalPages);
    // console.log(Math.ceil((currentPage - 1 / totalPages) * 100));

    return {
      lastCompletedPageProgress: Math.ceil((lastCompletedPage / totalPages) * 100),
      percent: Math.ceil(((currentPage - 1) / totalPages) * 100),
      string: `${this.state.currentPage} / ${totalPages}`
    }
  }

  canContinue(){
    const currentPage = this.currentPage();

    let allQuestionsHaveResponses = true;

    _.each(currentPage.questions || [], question => {
      const responseForQuestion = this.getQuestionResponse(question.id) || false;

      if(!responseForQuestion){
        allQuestionsHaveResponses = false;
      }

      //choice type
      //TODO support more dynamic object types here
      if(typeof responseForQuestion === 'object'){
        if(!responseForQuestion.top || !responseForQuestion.bottom){
          allQuestionsHaveResponses = false;
        }
      }
    });

    return allQuestionsHaveResponses;
  }

  goToNextPage(){

    const assessmentComplete = this.state.currentPage === this.state.assessmentPages.length;

    if(!assessmentComplete){
      this.setState({
        currentPage: this.state.currentPage + 1
      });
    }
    else{
      this.setState({
        showAssessmentComplete: true,
      });
    }
  }

  autofillLikertQuestions(){
    const responsesToAutofill = {};

    const likertQuestions = _.filter(this.orderedQuestions(), { questionFormat: 'likert' });

    _.each(likertQuestions, question => {
      responsesToAutofill[question.id] = Math.floor(Math.random() * 7) + 1;
    });

    this.props.updateQuestionResponses(responsesToAutofill, () => {

      this.setState({ savingProgress: true });

      this.props.saveProgress(() => {

        this.goToPageForLastRespondedToQuestion();

        this.setState({ savingProgress: false });
      });
    });
  }

  autofillLikertQuestionsOnPage(){
    const responsesToAutofill = {};

    const { questions } = this.currentPage();

    _.each(questions, question => {
      responsesToAutofill[question.id] = Math.floor(Math.random() * 7) + 1;
    });

    this.props.updateQuestionResponses(responsesToAutofill, () => {

      this.setState({ savingProgress: true });

      this.props.saveProgress(() => {

        this.goToNextPage();

        this.setState({ savingProgress: false });
      });
    });
  }

  autofillChoiceQuestions(){
    const responsesToAutofill = {};

    const choiceQuestions = _.filter(this.orderedQuestions(), { questionFormat: 'choice' });

    _.each(choiceQuestions, question => {

      // console.log('choice question: ', question);

      let choices = ['A','B','C','D'];

      const random1 = Math.floor(Math.random() * 100);
      const random2 = Math.floor(Math.random() * 100);

      const randomTop = choices[random1 % 4];

      choices.splice(random1 % 4, 1);

      const randomBottom = choices[random2 % 3];

      responsesToAutofill[question.id] = {
        top: randomTop,
        bottom: randomBottom,
      };
    });

    this.props.updateQuestionResponses(responsesToAutofill, () => {

      this.setState({ savingProgress: true });

      this.props.saveProgress(() => {

        this.goToPageForLastRespondedToQuestion();

        this.setState({ savingProgress: false });
      });
    });
  }

  autofillChoiceQuestionsOnPage(){
    const responsesToAutofill = {};

    const { questions } = this.currentPage();

    let choices = ['A','B','C','D'];

    const random1 = Math.floor(Math.random() * 100);
    const random2 = Math.floor(Math.random() * 100);

    const randomTop = choices[random1 % 4];

    choices.splice(random1 % 4, 1);

    const randomBottom = choices[random2 % 3];

    //only one question on page
    responsesToAutofill[questions[0].id] = {
      top: randomTop,
      bottom: randomBottom,
    };

    this.props.updateQuestionResponses(responsesToAutofill, () => {

      this.setState({ savingProgress: true });

      this.props.saveProgress(() => {

        this.goToNextPage();

        this.setState({ savingProgress: false });
      });
    });
  }

  saveProgressAndGoToNextPage(){
    this.setState({
      savingProgress: true
    });

    this.props.saveProgress(() => {
      this.setState({
        savingProgress: false
      });

      this.goToNextPage();
    });
  }

  saveProgressAndExit(){
    this.setState({
      savingProgress: true,
      exitingAfterSave: true
    });

    this.props.saveProgress(() => {
      this.setState({
        savingProgress: false
      });

      this.setState({
        currentPage: 0
      });
    });
  }

  exit(){
    this.setState({
      currentPage: 0
    });
  }

  renderAssessmentQuestionsPage(){

    const { savingProgress, exitingAfterSave } = this.state;

    const { questions, questionFormat, pageIndex } = this.currentPage();

    const pageProgress = this.getPageProgress();

    const showSaveAndExitButton = !savingProgress || (savingProgress && exitingAfterSave);
    const showSaveAndContinueButton = !savingProgress || !exitingAfterSave;

    return (
      <div id={`assessment-page-${pageIndex}`} className="assessment-page">
        <div className="question-group-explanation">{this.explanationForQuestionGroupType(questionFormat)}</div>
        <div className={`question-group ${questionFormat}`}>
          {/*<div className="question-group-top likert-scale">
            <div className="scale-label">Very Strongly Disagree</div>
            <div className="scale-label">Strongly Disagree</div>
            <div className="scale-label">Disagree</div>
            <div className="scale-label">Neutral</div>
            <div className="scale-label">Agree</div>
            <div className="scale-label">Strongly Agree</div>
            <div className="scale-label">Very Strongly Agree</div>
          </div>*/}
          {_.map(questions, this.renderQuestion.bind(this))}
        </div>

        <div className="page-actions">
          {showSaveAndContinueButton &&
            <button
              disabled={!this.canContinue() || savingProgress}
              onClick={this.saveProgressAndGoToNextPage.bind(this)}
              className="button blue-button"
            >{savingProgress ? <Localize id="btn_saving">Saving...</Localize> : <Localize id="btn_save_and_continue">Save & Continue</Localize>}</button>
          }
          {(emulatingDevelopment() || isDevelopment()) && showSaveAndContinueButton && questionFormat === 'likert' &&
            <React.Fragment>
              <button
                style={{marginRight: 10, marginTop: 7}}
                disabled={savingProgress}
                onClick={this.autofillLikertQuestions.bind(this)}
                className="button blue-button button-tiny"
              >{savingProgress ? <Localize id="btn_saving">Saving...</Localize> : <Localize id="btn_autocomplete">Autofill All</Localize>}</button>
              <button
                style={{marginRight: 10, marginTop: 7}}
                disabled={savingProgress}
                onClick={this.autofillLikertQuestionsOnPage.bind(this)}
                className="button blue-button button-tiny"
              >{savingProgress ? <Localize id="btn_saving">Saving...</Localize> : <Localize id="btn_autocomplete">Autofill Page</Localize>}</button>
            </React.Fragment>
          }
          {(emulatingDevelopment() || isDevelopment()) && showSaveAndContinueButton && questionFormat === 'choice' &&
            <React.Fragment>
              <button
                style={{marginRight: 10, marginTop: 7}}
                disabled={savingProgress}
                onClick={this.autofillChoiceQuestions.bind(this)}
                className="button blue-button button-tiny"
              >{savingProgress ? <Localize id="btn_saving">Saving...</Localize> : <Localize id="btn_autocomplete">Autofill All</Localize>}</button>
              <button
                style={{marginRight: 10, marginTop: 7}}
                disabled={savingProgress}
                onClick={this.autofillChoiceQuestionsOnPage.bind(this)}
                className="button blue-button button-tiny"
              >{savingProgress ? <Localize id="btn_saving">Saving...</Localize> : <Localize id="btn_autocomplete">Autofill Page</Localize>}</button>
          </React.Fragment>
          }
          {showSaveAndExitButton &&
            <button
              disabled={savingProgress}
              onClick={this.saveProgressAndExit.bind(this)}
              className="button grey-outline cancel-action"
            >{savingProgress ? <Localize id="btn_saving">Saving...</Localize> : <Localize id="btn_save_and_exit">Save & Exit</Localize>}</button>
          }
        </div>
        <div className="page-progress">
          <div className="progress-bar-container">
            <div className="progress-bar" style={{ width: pageProgress.percent + '%' }}></div>
          </div>
          <div className="progress-label">{pageProgress.string}</div>
        </div>
      </div>
    )
  }

  renderAssessmentPage(){

    if(this.currentPage().type === 'explanation'){
      return this.renderAssessmentExplanationPage();
    }
    else if(this.currentPage().type === 'questions'){
      return this.renderAssessmentQuestionsPage();
    }
    else{
      return null;
    }
  }

  completeAndSubmitAssessmentForScoring(){
    this.props.completeAndSubmitAssessmentForScoring();
  }

  renderResultsSubmittedModal(){
    const { showAssessmentComplete } = this.state;

    const { submittingAssessmentForScoring, activeLocalization } = this.props;

    const messageHtml = `
      <h2 style="margin-top:0px;">${localizeToString(activeLocalization, 'assessment_being_scored_header')}</h2>
      <p>${localizeToString(activeLocalization, 'assessment_being_scored_message')}</p>
      <p><small>${localizeToString(activeLocalization, 'assessment_being_scored_no_refresh_message')}</small></p>
      <div class="lds-ellipsis"><div></div><div></div><div></div><div></div></div>
    `;

    return (
      <MessageModal
        visible={showAssessmentComplete && submittingAssessmentForScoring}
        iconKey="question-insight"
        messageHtml={messageHtml}
        forceOpen={true}
      />
    )
  }

  renderAssessmentCompleteModal(){

    const { showAssessmentComplete } = this.state;
    const { orgDisplayName, submittingAssessmentForScoring, activeLocalization } = this.props;

    //https://loading.io/css/
    const messageHtml = `
      <h2 style="margin-top:0px;">${localizeToString(activeLocalization, 'assessment_complete_header')}</h2>
      <p>${localizeToString(activeLocalization, 'assessment_complete_instructions')}</p>
    `;

    return (
      <ConfirmModal
        visible={showAssessmentComplete && !submittingAssessmentForScoring}
        iconKey="question-insight"
        messageHtml={messageHtml}
        buttonClass="blue-button"
        confirmText={localizeToString(activeLocalization, 'btn_submit_assessment')}
        cancelText={localizeToString(activeLocalization, 'btn_cancel')}
        hide={() => { this.setState({ showAssessmentComplete: false }); }}
        onConfirm={this.completeAndSubmitAssessmentForScoring.bind(this)}
        confirming={this.props.completingAssessment}
        confirmingText={localizeToString(activeLocalization, 'btn_submitting_assessment')}
      />
    )
  }

  render(){

    const { currentPage, showAssessmentComplete} = this.state;
    const { assessment } = this.props;

    return (
      <div id="assessment-view" className="assessment-view">
        <div className="content-wrapper">
          {currentPage === 0 && this.renderInitialPage()}
          {currentPage > 0 && this.renderAssessmentPage()}
        </div>
        {this.renderAssessmentCompleteModal()}
        {this.renderResultsSubmittedModal()}
      </div>
    )
  }
}

const mapStateToProps = state => {
  return {
    activeLocalization: state.activeLocalization.data
  }
}

export default connect(mapStateToProps, {})(AssessmentView);



// WEBPACK FOOTER //
// ./src/components/takeAssessment/assessmentView.js